import { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { supabase } from '../lib/supabase'
import SEO from '../components/SEO'
import Turnstile from '../components/Turnstile'

export default function Login() {
  const navigate  = useNavigate()
  const location  = useLocation()
  const [searchParams] = useSearchParams()
  const { signIn, user, loading: authLoading } = useAuth()
  const toast = useToast()

  const reasonBanner = (() => {
    switch (searchParams.get('reason')) {
      case 'password_changed':
        return 'Password updated. Please sign in again.'
      case 'session_revocation_failed':
        return "Password updated. We couldn't sign out your other devices — sign out there manually if you're concerned."
      case 'session_invalidated':
        return "You've been signed out because your password was changed. Please sign in again."
      case 'banned':
        return 'This account has been banned. Contact support if you believe this is in error.'
      case 'delete_incomplete':
        return "Your account deletion was incomplete. Some of your data was removed, but your account couldn't be fully closed. Please contact support@versesite.com so we can finish closing it."
      case 'session_expired':
        return 'Your session expired. Please sign in again.'
      case 'login_required':
        return 'Sign in to continue.'
      default:
        return null
    }
  })()

  const [form, setForm]       = useState({ email: '', password: '' })
  const [error, setError]     = useState('')
  const [loading, setLoading] = useState(false)
  const [captchaToken, setCaptchaToken] = useState(null)

  const redirectTo = location.state?.from ?? '/'

  useEffect(() => {
    if (!authLoading && user) navigate(redirectTo, { replace: true })
  }, [user, authLoading])

  function handleChange(e) {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }))
    setError('')
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.email || !form.password) {
      setError('Please fill in all fields.')
      return
    }
    if (import.meta.env.VITE_TURNSTILE_SITE_KEY && !captchaToken) {
      setError('Please complete the verification.')
      return
    }
    setLoading(true)
    const { error: authError } = await signIn(form.email, form.password, captchaToken)
    if (authError) {
      setError(authError.message)
      setCaptchaToken(null)
      setLoading(false)
    } else {
      navigate(redirectTo, { replace: true })
    }
  }

  return (
    <div className="auth-page">
      <SEO title="Log In" description="Log in to your Verse account to read, write, and connect." noindex />
      {/* ── Left panel ── */}
      <div className="auth-panel">
        <div className="auth-panel__blobs">
          <div className="auth-panel__blob auth-panel__blob--1" />
          <div className="auth-panel__blob auth-panel__blob--2" />
          <div className="auth-panel__blob auth-panel__blob--3" />
        </div>

        <div className="auth-panel__content">
          <Link to="/" className="auth-panel__logo">
            <img src="/verse-icon.png" alt="Verse" className="auth-panel__logo-img" />
            <span>Verse</span>
          </Link>

          <blockquote className="auth-panel__quote">
            "Every great story starts with a single word. Yours is waiting."
          </blockquote>

          <div className="auth-panel__features">
            {[
              'Write and publish your stories for free.',
              'Reach readers from around the world.',
              'Build a community around your work.',
              'Explore thousands of fandoms.',
            ].map((text) => (
              <div key={text} className="auth-panel__feature">{text}</div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Right: form ── */}
      <div className="auth-form-panel">
        <div className="auth-form-wrap">
          <div className="auth-form-header">
            <h1 className="auth-form-title">Welcome back</h1>
            <p className="auth-form-subtitle">Sign in to continue your story</p>
          </div>

          <div className="auth-social-btns">
            <button className="auth-social-btn" onClick={async () => {
              try { sessionStorage.setItem('verse_signin_intent', '1') } catch {}
              const { error } = await supabase.auth.signInWithOAuth({ provider: 'google', options: { redirectTo: window.location.origin } })
              if (error) {
                try { sessionStorage.removeItem('verse_signin_intent') } catch {}
                toast(error.message, 'error')
              }
            }}>
              <GoogleIcon /> Continue with Google
            </button>
          </div>

          <div className="auth-divider"><span>or sign in with email</span></div>

          <form className="auth-form" onSubmit={handleSubmit} noValidate>
            {reasonBanner && <div className="auth-error">{reasonBanner}</div>}
            {error && <div className="auth-error">{error}</div>}

            <div className="auth-field">
              <label className="auth-label" htmlFor="email">Email</label>
              <input
                id="email" name="email" type="email"
                className="auth-input"
                placeholder="you@example.com"
                value={form.email}
                onChange={handleChange}
                autoComplete="email"
              />
            </div>

            <div className="auth-field">
              <div className="auth-label-row">
                <label className="auth-label" htmlFor="password">Password</label>
                <a href="/forgot-password" className="auth-forgot">Forgot password?</a>
              </div>
              <input
                id="password" name="password" type="password"
                className="auth-input"
                placeholder="••••••••"
                value={form.password}
                onChange={handleChange}
                autoComplete="current-password"
              />
            </div>

            <Turnstile onToken={setCaptchaToken} onExpire={() => setCaptchaToken(null)} />

            <button type="submit" className="btn btn-primary auth-submit" disabled={loading}>
              {loading ? <Spinner /> : 'Sign in'}
            </button>
          </form>

          <p className="auth-switch">
            Don't have an account?{' '}
            <Link to="/signup" className="auth-switch-link">Create one free</Link>
          </p>
        </div>
      </div>
    </div>
  )
}


function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
      <path d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  )
}

function Spinner() {
  return (
    <span className="auth-spinner" aria-label="Loading">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" strokeLinecap="round"/>
      </svg>
    </span>
  )
}
